<script setup lang="ts">
import stores from './storage/stores'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
    applyTheme,
    isOtherThemeHappy,
    isThemeDefault,
    isThemeHappy,
    isThemeNoct
} from './event/theme/theme'
import { loadData } from './utils/loadData'
import { dataLoaded } from './event/dataLoaded'
import { watchersApp } from './utils/watchers/app/watchersApp'
import { formatStoreName } from '@/utils/store/formatStoreName'
import { showSectionManagement } from './event/main/app/showSectionManagement'
import { useHead } from '@vueuse/head'
import { url } from './event/url/url'
import DefaultLayout from './layout/DefaultLayout.vue'
import HappyLayout from './layout/HappyLayout.vue'
import { detectClient } from './utils/config/detectClient'
import { getStatusStoreHappy } from './happyTheme/utils/store/getStatusStoreHappy'
import { checkAndRefreshTokenV2 } from './utils/jwt/checkAndRefreshTokenV2'
import ButtonThemeLocal from './components/main/ButtonThemeLocal.vue'
import DevThemeSwitcher from './components/dev/DevThemeSwitcher.vue'

const isDev = import.meta.env.DEV

const Management = defineAsyncComponent(
    () => import('@/components/management/Management.vue')
)

const route = useRoute()

const clientHost = detectClient()

interface interfaceClient {
    hostname: string
    url: string
    urlBack: string
    telephone: string
    favicon: string
    favicon_alt: string
    title: string
    description: string
    facebook: string
    instagram: string
    tiktok: string
}
const client = ref<interfaceClient>({
    hostname: '',
    url: '',
    urlBack: '',
    telephone: '',
    favicon: '',
    favicon_alt: '',
    title: '',
    description: '',
    facebook: '',
    instagram: '',
    tiktok: ''
})

// Route name -> i18n key (resolved reactively via locale change)
const routeTitleMapName: Record<string, string> = {
    'catalog-product': 'productCatalog',
    product: 'productDetails',
    basket: 'cart',
    login: 'login',
    register: 'registerTitle',
    'confirm-register': 'confirmRegistration',
    forget: 'forgotPasswordTitle',
    'basket-order': 'cartOrderTitle',
    'success-payment': 'paymentSuccess',
    'cancel-payment': 'paymentError',
    dashboard: 'dashboard',
    'dashboard-edit': 'dashboardEditInfo',
    'dashboard-password': 'dashboardEditPassword',
    'dashboard-address': 'dashboardAddresses',
    'dashboard-order': 'dashboardOrders',
    'cancel-delete': 'cancelAccountDeletion',
    'privacy-policy': 'privacyTitle',
    'condition-use': 'cguTitle'
}

const { t, locale } = useI18n()

const pageTitle = computed(() => {
    // Touch locale so the computed re-runs on language change
    void locale.value
    const routeName = route.name as string
    const titleKey = routeTitleMapName[routeName]
    const title = titleKey ? `| ${t(titleKey)}` : ''
    return `${client.value?.title} ${title}`
})

const canonicalUrl = computed(() => {
    const path = route.path
    const returnCanonicalUrl = `${url.protocol}${url.host}${path}`
    return returnCanonicalUrl
})

useHead({
    title: pageTitle,
    meta: [{ name: 'description', content: computed(() => client.value?.description) }],
    link: [
        { rel: 'icon', href: computed(() => `${client.value?.urlBack}${client.value?.favicon}`) },
        {
            rel: 'apple-touch-icon',
            href: computed(() => `${client.value?.urlBack}${client.value?.favicon}`)
        },
        {
            rel: 'canonical',
            href: canonicalUrl
        }
    ]
})

async function getMetadataClient() {
    const res = await fetch('/clientConfig.json')
    const clientConfigData = await res.json()

    const foundClient = clientConfigData.find((c: any) => c.hostname === clientHost)

    if (foundClient) {
        client.value = foundClient
    } else {
        client.value = clientConfigData[0]
    }
}

getMetadataClient()
// const client = Object.values(clientConfig).find((client) => client.hostname === clientId)

watchersApp(route)

onMounted(async () => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
        applyTheme(savedTheme)
    }

    await loadData()

    let initialLoad = true
    if (isThemeHappy.value || isOtherThemeHappy.value) {
        checkAndRefreshTokenV2()
    }

    document.addEventListener('visibilitychange', async () => {
        if (document.visibilityState === 'visible' && !initialLoad) {
            await loadData()
            if (isThemeHappy.value || isOtherThemeHappy.value) {
                getStatusStoreHappy()

                checkAndRefreshTokenV2()
            }
        }

        initialLoad = false
    })

    watch([() => route.name, () => dataLoaded.value], ([routeName, dataLoadedValue]) => {
        if (dataLoadedValue === 2) {
            const store = stores.state.storeData.name
            if (store) {
                const storeName = formatStoreName(store)
                const cartKey = `${storeName}_cart`

                if (routeName === 'success-payment') {
                    localStorage.removeItem(cartKey)
                }
            }
        }
    })
})
</script>

<template>
    <div class="content-site" v-if="showSectionManagement === false">
        <ButtonThemeLocal v-if="isThemeNoct" />
        <DefaultLayout v-if="isThemeDefault" />
        <HappyLayout v-if="isThemeHappy || isOtherThemeHappy" />
    </div>

    <Management v-else />

    <DevThemeSwitcher v-if="isDev" />
</template>

<style scoped>
.content-site {
    @apply flex flex-col items-center justify-between w-full h-full font-satoshi happy-light:font-montserrat happy-dark:font-montserrat;
}
</style>
